<template>
  <div class="product-details-wrapper">
    <div class="media-area" :class="{ 'is-model-loading': showModelLoadingMessage }">
      <!-- View toggles styled like option headers (animated underline tabs) -->
      <div class="image-view-tabs">
        <md-tabs ref="viewTabs" :md-active-tab="activeViewTab" @md-changed="onViewTabChanged">
          <md-tab id="image" md-label="Base Model Image" :md-ripple="false" />
          <md-tab
            id="model"
            :md-label="vaultFound === false ? 'Request CAD Model' : 'CAD Model'"
            :md-ripple="false"
          />
        </md-tabs>
        <span v-if="vaultFound === false" class="tab-info" aria-label="3D model unavailable">
          <font-awesome-icon :icon="['fas', 'circle-info']" />
          <md-tooltip md-direction="bottom">
            A 3D view of this part isn't available right now.
          </md-tooltip>
        </span>
      </div>
      <div
        id="staticImageContainer"
        class="product-details-image"
        v-show="!showing3D"
      >
        <div key="standard" class="image-wrap">
          <img :src="currentProductImage" :alt="productDetails.PartID">
          <p class="image-caption" v-if="productDetails.FeaturedImageCaption">
            {{ productDetails.FeaturedImageCaption }}
          </p>
        </div>
      </div>
      <div
        id="viewerContainer"
        :style="{ display: showing3D ? 'flex' : 'none' }"
      ></div>
      <transition name="media-loading-fade">
        <div v-if="showModelLoadingMessage" class="media-loading-message" aria-live="polite">
          <span class="media-loading-spinner" aria-hidden="true"></span>
          <span>CAD Model Loading...</span>
        </div>
      </transition>
    </div>

    <!-- Actions remain below the image/viewer area -->
    <div class="image-actions">
      <md-snackbar
        md-persistent
        :md-position="position"
        :md-duration="duration"
        :md-active.sync="isCadDownloading"
      >
        <div class="inner-popover">
          <span class="popover-description">
            Your file is being generated and will begin downloading soon.
          </span>
        </div>
      </md-snackbar>  
      <md-button
        class="md-outline"
        v-if="vaultFound === true"
        @click="onDownloadCadClick"
      >
        <font-awesome-icon :icon="['fas', 'download']" />
        Download CAD (step file)
      </md-button>
      <md-snackbar
        md-persistent
        :md-position="position"
        :md-duration="duration"
        :md-active.sync="isPDFDownloading"
      >
        <div class="inner-popover">
          <span class="popover-description">
            Your datasheet is being generated and will begin downloading soon.
          </span>
        </div>
      </md-snackbar>
      <md-button
        v-if="productDetails.IsValidPart"
        class="md-outline"
        @click="onDownloadPDFClick"
      >
        <font-awesome-icon :icon="['fas', 'file-pdf']" />
        PDF Datasheet
      </md-button>
      <md-button
        v-if="isValidCadUser"
        class="md-outline"
        @click="onModifyPreferences"
      >
        <font-awesome-icon :icon="['fas', 'gear']" />
        Update Profile
      </md-button>
    </div>
  </div>
</template>

<script>
import ViewerModule from '../../utilities/viewer';
import { CAD_VIEWER_ENABLED } from '../../utilities/featureFlags';


export default {
  name: 'product-image',
  props: {
    partId: {
      type: [String, Number],
      required: true
    },
    productDetails: {
      type: Object,
      required: true
    },
    isValidCadUser: {
      type: Boolean,
      default: false
    },
    cadUserEmail: {
      type: String,
      default: null
    },
    cadUserFullName: {
      type: String,
      default: null
    },
    cadUserCompany: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      cadViewerEnabled: CAD_VIEWER_ENABLED,
      showCadModal: false,
      existingEmail: null,        // Already Registered
      newCadUserEmail: null,      // New Registration

      showing3D: false,
      vaultFound: CAD_VIEWER_ENABLED ? null : false,
      showModelLoadingMessage: false,
      loadingDelayTimer: null,
      activeModelLoadCount: 0,
      partLoadRequestId: 0,
      hasLoaded3DOnce: false,
      // snackbar + downloads
      isCadDownloading: false,
      isPDFDownloading: false,
      duration: 6000,
      position: 'center'
    };
  },
  computed: {
    currentProductImage() {
      return this.productDetails.FeaturedImageURL;
    },
    emailRequestHref() {
      return `mailto:cad@hamiltoncaster.com?Subject=Request CAD for Part ID ${this.partId}`;
    },
    activeViewTab() {
      return this.showing3D ? 'model' : 'image';
    }
  },
  methods: {
    beginModelLoading() {
      this.activeModelLoadCount += 1;
      if (this.activeModelLoadCount === 1) {
        if (this.loadingDelayTimer) {
          clearTimeout(this.loadingDelayTimer);
        }
        this.showModelLoadingMessage = false;
        this.loadingDelayTimer = setTimeout(() => {
          if (this.activeModelLoadCount > 0) {
            this.showModelLoadingMessage = true;
          }
        }, 300);
      }
    },
    endModelLoading() {
      if (this.activeModelLoadCount > 0) {
        this.activeModelLoadCount -= 1;
      }
      if (this.activeModelLoadCount === 0) {
        if (this.loadingDelayTimer) {
          clearTimeout(this.loadingDelayTimer);
          this.loadingDelayTimer = null;
        }
        this.showModelLoadingMessage = false;
      }
    },
    isLatestPartLoad(requestId) {
      return requestId === this.partLoadRequestId;
    },
    onViewTabChanged(id) {
      if (id === 'image') {
        this.showImage();
      } else if (id === 'model') {
        if (this.vaultFound) {
          this.show3D();
        } else {
          // Open email request for CAD and keep selection on image when 3D not available
          const href = this.emailRequestHref;
          try {
            window.location.href = href;
          } catch (e) {}
          this.showImage();
          // Immediately revert the tab selection to 'image' to avoid visual indicator change
          const tabsRef = this.$refs && this.$refs.viewTabs;
          if (tabsRef && typeof tabsRef.setActiveTab === 'function') {
            try { tabsRef.setActiveTab('image'); } catch (e) {}
          }
        }
      }
    },
    showImage() {
      if (this.showing3D) {
        this.showing3D = false;
      }
    },
    async show3D() {
      if (!this.cadViewerEnabled) return;
      if (!this.showing3D) {
        if (!this.vaultFound) return; // guard when 3D not available
        await this.toggleViewer();
      }
    },
    async toggleViewer() {
      this.showing3D = !this.showing3D;
      if (this.showing3D) {
        await this.$nextTick();
        let startedLoadingForToggle = false;
        let endedLoadingEarly = false;
        try {
          // Only fetch/download files and load the model if not already done
          if (!ViewerModule.svfUrls || !ViewerModule.svfUrls.option1) {
            startedLoadingForToggle = true;
            this.beginModelLoading();
            // Ensure vault data is loaded for the current base part before attempting downloads
            await ViewerModule.loadVaultData(this.partId);
            this.vaultFound = ViewerModule.vaultFound === true;
            await ViewerModule.getAndDownloadFiles(0, 1);
          }
          if (startedLoadingForToggle) {
            this.endModelLoading();
            endedLoadingEarly = true;
          }
          const showViewerSuccess = await ViewerModule.showViewer();
          if (showViewerSuccess !== false) {
            this.hasLoaded3DOnce = true;
          }
        } catch (e) {
          console.error('Error loading viewer while toggling to 3D:', e);
          this.vaultFound = false;
          this.showImage();
        } finally {
          if (startedLoadingForToggle && !endedLoadingEarly) {
            this.endModelLoading();
          }
        }
      }
    },

    triggerCadDownload (email) {
      this.isCadDownloading = true
      fetch('/DesktopModules/AcuitiSolutions/CatalogDetail/API/List/LogCadDownloadIntent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Email: email,
          FullName: this.cadUserFullName || null,
          Company: this.cadUserCompany || null,
          PartId: this.productDetails.PartID,
          FileType: 'STEP',
          PageContext: 'CAD Download - Product Detail Page'
        })
      }).catch(() => {})
      ViewerModule.downloadSTP()
      setTimeout(() => { this.isCadDownloading = false }, this.duration)
    },
    onDownloadCadClick () {
      const email = this.cadUserEmail || null

      if (!email) {
        this.$emit('display-cad-modal', true)
        return
      }

      this.triggerCadDownload(email)
    },

    onDownloadPDFClick() {
      this.isPDFDownloading = true;
      const host = process.env.NODE_ENV === 'development' ? 'https://beta.hamiltoncaster.com' : '';
      const pdfUrl = '/DesktopModules/AcuitiSolutions/CatalogDetail/API/List/GetDataSheetPDF';
      const filePath = `${host}${pdfUrl}?partId=${this.partId}`;

      const a = document.createElement('a');
      a.href = filePath;
      a.download = filePath.substr(filePath.lastIndexOf('/') + 1);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => { this.isPDFDownloading = false; }, this.duration);
    },
    onModifyPreferences() {
      this.$emit('display-cad-modal', true);
    }
  },
  mounted: async function() {
    // On initial load, always show the image and do not perform any model checks.
    this.showing3D = false;
    if (!this.cadViewerEnabled) {
      this.vaultFound = false;
      return;
    }
    // Preload vault metadata so the 3D tab can work immediately if clicked.
    try {
      await ViewerModule.loadVaultData(this.partId);
      this.vaultFound = ViewerModule.vaultFound === true;
    } catch (e) {
      console.warn('Preload vault data failed (non-blocking):', e);
      this.vaultFound = false;
    }

    // No intercepted clicks needed; we handle unavailable state in onViewTabChanged
  },
  beforeDestroy() {
    if (this.loadingDelayTimer) {
      clearTimeout(this.loadingDelayTimer);
      this.loadingDelayTimer = null;
    }
  },
  watch: {
    // Reload vault data and files when the Part ID changes
    async partId(newVal) {
      if (!this.cadViewerEnabled) {
        this.vaultFound = false;
        this.showImage();
        return;
      }
      const requestId = ++this.partLoadRequestId;
      const suppressLoadingForPreviewSwitch = this.showing3D && this.hasLoaded3DOnce;
      let loadingStartedForRequest = false;
      try {
        // Reset any existing viewer models and cached URLs before loading new part assets
        if (typeof ViewerModule.reset === 'function') {
          ViewerModule.reset();
        }
        await ViewerModule.loadVaultData(newVal);
        if (!this.isLatestPartLoad(requestId)) return;
        this.vaultFound = ViewerModule.vaultFound === true;

        if (!suppressLoadingForPreviewSwitch && this.vaultFound) {
          this.beginModelLoading();
          loadingStartedForRequest = true;
        }

        await ViewerModule.getAndDownloadFiles(0, 1);
        if (!this.isLatestPartLoad(requestId)) return;
        // Auto-switch/refresh based on availability
        if (ViewerModule.svfUrls && ViewerModule.svfUrls.option1) {
          if (this.showing3D) {
            await ViewerModule.loadModel('option1');
          } else {
            await this.show3D();
          }
        } else {
          this.showImage();
        }
      } catch (e) {
        if (this.isLatestPartLoad(requestId)) {
          console.error('Error reloading viewer data for new Part ID:', e);
          this.vaultFound = false;
          this.showImage();
        }
      } finally {
        if (loadingStartedForRequest) {
          this.endModelLoading();
        }
      }
    }
  }
};
</script>

<style lang="scss">
  @import "../../assets/variables";

  $mobileImageSize:   21.875rem;
  $standardImageSize: 31.25rem;

  .product-details-wrapper {
    position: relative;
    /* Shared height for image and viewer containers */
    --mediaHeight: #{$mobileImageSize};
    @media screen and (min-width: $medium) {
      --mediaHeight: #{$standardImageSize};
    }

    .media-area {
      width: 100%;
      position: relative;

      &.is-model-loading {
        .product-details-image {
          opacity: 0.45;
        }
      }
    }

    .media-loading-message {
      position: absolute;
      left: 50%;
      top: 56%;
      transform: translate(-50%, -50%);
      z-index: 20;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      background-color: rgba($white, 0.95);
      color: $primaryColor;
      border: 1px solid $borderColor;
      border-radius: 9999px;
      padding: 0.5rem 0.875rem;
      font-size: 1rem;
      font-weight: 600;
      pointer-events: none;
      white-space: nowrap;
    }

    .media-loading-spinner {
      width: 0.9rem;
      height: 0.9rem;
      border-radius: 50%;
      border: 2px solid rgba($primaryColor, 0.25);
      border-top-color: $primaryColor;
      animation: cad-loading-spin 0.8s linear infinite;
    }

    @keyframes cad-loading-spin {
      to {
        transform: rotate(360deg);
      }
    }

    .media-loading-fade-enter-active,
    .media-loading-fade-leave-active {
      transition: opacity 0.25s ease, transform 0.25s ease;
    }

    .media-loading-fade-enter,
    .media-loading-fade-leave-to {
      opacity: 0;
      transform: translate(-50%, calc(-50% + 0.25rem));
    }
    .product-details-image {
      text-align: center;
      min-height: var(--mediaHeight);
      height: var(--mediaHeight);
      margin-bottom: 2rem;

      .image-wrap {
        img {
          width: 100%;
        }
      }

      img {
        max-width: var(--mediaHeight);
        max-height: var(--mediaHeight);
      }

      .image-caption {
        font-size: 1rem;
        font-style: italic;
        text-align: center;
      }

    }
  }

  /* Tabs header mirroring option headers: baseline spans full media width with animated indicator */
  .image-view-tabs {
    position: relative;
    display: flex;
    justify-content: flex-start; /* left aligned */
    margin-bottom: .5rem;
    /* Full-width grey baseline */
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 1px;
      background-color: $borderColor;
      pointer-events: none;
    }

    /* Non-intrusive info icon shown when 3D is unavailable */
    .tab-info {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      display: inline-flex;
      align-items: center;
      color: $primaryColor;
      z-index: 2; /* above baseline */
      .md-tooltip {
        white-space: normal;
        max-width: 18rem;
      }
    }

    .md-tabs {
      width: 100%;
      max-width: 100%;
      &.md-theme-default {
        .md-tabs-navigation {
          background-color: transparent;
          border-bottom: none; // baseline drawn by parent ::after
          display: flex;
          justify-content: flex-start; /* left aligned */
        }
      }

      .md-button {
        font-size: 1.5rem;            /* requested size */
        color: $primaryColor;          /* unselected = red */
        text-transform: none;
        opacity: 1;
        transition: color .2s ease, background-color .2s ease;
        &:hover {
          background-color: $primaryColor; /* red background on hover */
          color: $white;                    /* white text on hover */
        }
        &.md-active {
          color: #000; /* selected = black */
        }
      }

      /* Override inner content font-size & color (Vue Material sets .md-button-content to .875rem) */
      .md-button .md-button-content {
        font-size: 1.5rem !important; /* increase specificity, ensure override */
        line-height: 1.5rem;
        color: inherit; /* follow parent button color rules */
      }
      .md-button.md-active .md-button-content {
        color: rgba(109, 110, 112, 1);
      }
      .md-button:hover .md-button-content {
        color: $white;
      }
      /* Ensure unselected (non-hover, non-active) tabs always show red text */
      .md-button:not(.md-active):not(:hover) .md-button-content {
        color: $primaryColor !important;
      }

      /* Remove ripple overlay tint so hover bg shows true color */
      .md-button:not([disabled]).md-focused:before,
      .md-button:not([disabled]):active:before,
      .md-button:not([disabled]):hover:before {
        opacity: 0;
      }

      .md-tabs-indicator {
        background-color: #000; // black animated underline for active tab
      }
    }
  }

  .image-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: .5rem;
    margin-top: .25rem;
  }

  /* Ensure the viewer container matches the image container size */
  #viewerContainer {
    width: 100%;
    height: var(--mediaHeight);
    position: relative;
  }

  /* Make the Forge Viewer fill the container */
  #viewerContainer > .adsk-viewing-viewer {
    width: 100% !important;
    height: 100% !important;
    position: absolute;
    top: 0; left: 0;
  }
</style>
